import creditCardType from "credit-card-type";

import { cardNumberValidator } from "cc-tools/card-number-validator";
import { niceTypesToCreditCardTypeCardBrandId } from "cc-tools/helpers";

import { PayByBankAccountType } from "checkout-experience/components/payment-method/pay-options/pay-by-bank/types";
import { PaymentMethodDisplay } from "checkout-experience/components/payment-method/payment-method-summary-display";
import { PaymentOption } from "checkout-experience/components/payment-method/payment-option-provider";
import { PaymentMessages } from "checkout-query-client/api/types";
import { ChexFieldValues } from "checkout-query-client/types/chex-form-fields";
import { PaymentType } from "checkout-query-client/types/form-types";

import {
  CardNetwork,
  EcpBankAccountType,
  PaymentMethodSummary,
  PlainMessage,
} from "frontend-client";

// TODO: Update this mapping when new Saved payment method type is introduced
export const getPaymentTypeOfSavedPaymentMethods = (
  paymentMethod: PlainMessage<PaymentMethodSummary> | undefined
) => {
  if (!paymentMethod || !paymentMethod.paymentMethod) {
    return PaymentType.creditCard;
  }
  switch (paymentMethod.paymentMethod.case) {
    case "card":
      return PaymentType.creditCard;
    case "electronicCheck":
      return PaymentType.eCheck;
    case "sepa":
      return PaymentType.sepa;
    case "payByBank":
      return PaymentType.payByBank;
    default:
      return PaymentType.creditCard;
  }
};

export const createSelectedPaymentDisplay = (
  paymentOption: PaymentOption,
  fieldValues?: ChexFieldValues,
  savedPaymentMethods?: Array<PlainMessage<PaymentMethodSummary>>,
  paymentMessages?: PaymentMessages
): PaymentMethodDisplay => {
  const { paymentType, formId } = paymentOption;
  if (paymentType.toString() === formId) {
    const fieldValue = {
      [PaymentType.creditCard]: fieldValues?.cardNumber, // form.fields?.cardNumber?.value ?? "",
      [PaymentType.eCheck]: fieldValues?.accountNumber, // form.fields?.accountNumber?.value ?? "",
      [PaymentType.sepa]: fieldValues?.iban,
      [PaymentType.paze]: fieldValues?.pazeLastFour,
      [PaymentType.giftCard]: fieldValues?.giftCardNumber,
    };
    type FieldValueKey = keyof typeof fieldValue;

    let last4 = fieldValue[paymentType as FieldValueKey];
    last4 = last4 ? last4.substring(last4.length - 4) : "";

    const niceType: string | undefined = fieldValues?.selectNetwork
      ? creditCardType.getTypeInfo(
          niceTypesToCreditCardTypeCardBrandId[fieldValues?.selectNetwork]
        )?.niceType
      : cardNumberValidator(fieldValues?.cardNumber).cardType?.niceType;

    const { expirationMonth, expirationYear } = getExpirationMonthYear(
      fieldValues?.expiration
    );
    const bankAccountType =
      paymentType === PaymentType.eCheck
        ? (fieldValues?.accountType as unknown as EcpBankAccountType)
        : undefined;

    if (paymentType === PaymentType.creditCard) {
      return {
        network: niceType,
        last4,
        expirationMonth,
        expirationYear,
        ...paymentOption,
      };
    } else if (paymentType === PaymentType.eCheck) {
      return {
        ddaAccountNumberLast4: last4,
        bankAccountType,
        paymentType,
      };
    } else if (paymentType === PaymentType.sepa) {
      return {
        ibanLast4: last4,
        paymentType,
      };
    } else if (paymentType === PaymentType.paze) {
      return {
        last4,
        network: fieldValues?.network,
        paymentType,
      };
    } else if (paymentType === PaymentType.payByBank) {
      return {
        ...paymentMessages?.payByBankCompletionMessage?.payload?.payResult
          ?.paymentMethod?.payByBank,
        paymentType,
      };
    } else if (
      [PaymentType.googlePay, PaymentType.applePay].includes(paymentType)
    ) {
      return { paymentType };
    } else if (paymentType === PaymentType.giftCard) {
      return {
        last4,
        paymentType,
      };
    }

    return {
      paymentType,
    };
  } else {
    const { expirationMonth, expirationYear } = getExpirationMonthYear(
      fieldValues?.expiration
    );
    const paymentMethodSummary: PlainMessage<PaymentMethodSummary> | undefined =
      savedPaymentMethods?.find((p) => p.paymentMethodId === formId);
    const updateExpiration =
      paymentType === PaymentType.creditCard && expirationMonth
        ? {
            expirationMonth,
            expirationYear,
          }
        : {};

    return {
      ...paymentMethodSummary?.paymentMethod.value,
      ...updateExpiration,
      paymentType,
    };
  }
};

export const isPaymentMethodExpired = ({
  expirationMonth,
  expirationYear,
}: {
  expirationMonth?: number;
  expirationYear?: number;
}) => {
  const [month, year] = [
    new Date().getMonth(),
    Number(new Date().getFullYear()),
  ];

  return Boolean(
    expirationYear &&
      expirationMonth &&
      (expirationYear < year ||
        (expirationYear === year && expirationMonth < month + 1))
  );
};

export const getExpirationMonthYear = (expirationValue?: string) => {
  const [expirationMonth, expirationYear] = (expirationValue || "")
    .split(" / ")
    .map(Number);

  if (expirationMonth && expirationYear) {
    return {
      expirationMonth,
      expirationYear: expirationYear + 2000,
    };
  }

  return {};
};

export const getSupportedCardTypesListAsGoogleStrings = (
  cards: Array<CardNetwork>
): google.payments.api.CardNetwork[] => {
  const supportedCards: google.payments.api.CardNetwork[] = [];

  for (const card of cards) {
    switch (card) {
      case CardNetwork.AMEX:
        supportedCards.push("AMEX");
        break;
      case CardNetwork.DISCOVER:
        supportedCards.push("DISCOVER");
        break;
      case CardNetwork.MASTERCARD:
        supportedCards.push("MASTERCARD");
        break;
      case CardNetwork.VISA:
        supportedCards.push("VISA");
        break;
      case CardNetwork.JCB:
        supportedCards.push("JCB");
        break;
    }
  }

  return supportedCards;
};

export const getSupportedCardTypesListAsAppleStrings = (
  cards: Array<CardNetwork>
): Array<string> => {
  const supportedCards: Array<string> = [];

  for (const card of cards) {
    switch (card) {
      case CardNetwork.AMEX:
        supportedCards.push("amex");
        break;
      case CardNetwork.DISCOVER:
        supportedCards.push("discover");
        break;
      case CardNetwork.MASTERCARD:
        supportedCards.push("masterCard");
        break;
      case CardNetwork.VISA:
        supportedCards.push("visa");
        break;
      case CardNetwork.JCB:
        supportedCards.push("jcb");
        break;
      case CardNetwork.MAESTRO:
        supportedCards.push("maestro");
        break;
      case CardNetwork.CHINA_UNION_PAY:
        supportedCards.push("chinaUnionPay");
        break;
    }
  }

  return supportedCards;
};

export const isFormValueExpired = (
  expiration: string | undefined,
  expirationMonth: string | undefined,
  expirationYear: string | undefined
) => {
  let formValueExpiration = {};
  if (expiration) {
    formValueExpiration = getExpirationMonthYear(expiration);
  } else if (expirationMonth && expirationYear) {
    formValueExpiration = {
      expirationMonth: +expirationMonth,
      expirationYear: +expirationYear,
    };
  }

  return isPaymentMethodExpired(formValueExpiration);
};

export const getPayByBankAccountTypeString = (accountType: string) => {
  let accountTypeString = "";
  switch (accountType as PayByBankAccountType) {
    case PayByBankAccountType.DYNAMIC_DEPOSIT_ACCOUNT_TYPE_SAVING:
      accountTypeString = "Savings" + " ";
      break;
    case PayByBankAccountType.DYNAMIC_DEPOSIT_ACCOUNT_TYPE_CHECKING:
      accountTypeString = "Checking" + " ";
      break;
    case PayByBankAccountType.DYNAMIC_DEPOSIT_ACCOUNT_TYPE_CORPORATE_CHECKING:
      accountTypeString = "Corporate Checking" + " ";
      break;
    case PayByBankAccountType.DYNAMIC_DEPOSIT_ACCOUNT_TYPE_UNSPECIFIED:
      accountTypeString = "Unspecified" + " ";
      break;
  }
  return accountTypeString;
};
